import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Chrome, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import EthnicityMultiSelect from '@/components/EthnicityMultiSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // role/intent now collected via /onboarding/intent multi-select AFTER signup.
  // We keep `intent` (vendor / hire / builder) only as a query-string hint to
  // pre-check the right box on the intent picker.
  const [intentHint] = useState(searchParams.get('intent') || '');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStep, setForgotStep] = useState('email');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  // Has the user already triggered a Meta "Lead" event for this session?
  // We only want to fire Lead once per signup attempt so we don't blow up the
  // pixel with hundreds of identical events per visitor.
  const [leadFired, setLeadFired] = useState(false);

  // Lead: fires the *first time* a registering user types into ANY signup
  // field. Tells Meta "this person is intent-deep enough to count as a lead"
  // for ad optimization — without waiting for them to actually finish.
  const fireLead = () => {
    if (leadFired || mode !== 'register') return;
    setLeadFired(true);
    try {
      window.metaTrack?.('Lead', {
        content_name: 'Signup Form Started',
        mode,
        currency: 'USD',
        value: 0,
      });
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    if (redirecting) return;

    if (user) {
      const returnPath = searchParams.get('return') || (typeof window !== 'undefined' ? sessionStorage.getItem('postAuthRedirect') : null);
      if (returnPath && returnPath.startsWith('/')) {
        try { sessionStorage.removeItem('postAuthRedirect'); } catch {}
        navigate(returnPath, { replace: true });
        return;
      }
      // If they signed up but haven't picked an intent yet, send them there.
      // `intent_chosen` is set true on /api/auth/set-intent and persisted on
      // the user document.
      if (user.intent_chosen === false) {
        navigate('/onboarding/intent', { replace: true });
        return;
      }
      if (user.role === 'vendor' || user.roles?.includes('vendor')) {
        navigate('/dashboard/vendor', { replace: true });
      } else {
        const from = location.state?.from?.pathname || '/browse';
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, location, redirecting, searchParams]);

  // (role/ethnicity/dob are no longer collected at signup — see intent picker)

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        if (!firstName.trim() || !lastName.trim()) { toast.error('Please enter your first and last name'); setLoading(false); return; }
        if (!email.trim()) { toast.error('Please enter your email'); setLoading(false); return; }
        if (password.length < 6) { toast.error('Password must be at least 6 characters'); setLoading(false); return; }
        if (!agreedToTerms) { toast.error('Please agree to the Terms & Conditions to continue'); setLoading(false); return; }

        const fullName = `${firstName.trim()} ${lastName.trim()}`;
        // Everyone signs up as `consumer` by default. The intent picker on the
        // very next page upgrades roles (vendor / website_builder) based on
        // multi-select. Keeps the signup form to 4 fields exactly.
        const regRole = 'consumer';
        setRedirecting(true);
        const regData = await register(email, password, fullName, regRole, {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        });
        toast.success('Account created! Please verify your email.');

        try {
          window.metaTrack?.('CompleteRegistration', {
            content_name: 'Signup',
            role: regRole,
            status: 'completed',
            currency: 'USD',
            value: 0,
          });
        } catch { /* non-blocking */ }

        if (subscribeNewsletter) {
          try {
            const API = process.env.REACT_APP_BACKEND_URL + '/api';
            await fetch(`${API}/newsletter/signup`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: email.trim(),
                first_name: firstName.trim() || null,
                role: regRole,
                subscribe_consent: true,
              }),
            });
          } catch {/* silent */}
        }

        if (regData?.promo_code) {
          localStorage.setItem('my_promo_code', regData.promo_code);
        }

        // Every signup now lands on the intent picker. The picker handles
        // role upgrade + final routing. Pass along the hint so the right box
        // is pre-checked (e.g. "Become a Vendor" from homepage hero).
        const dest = intentHint ? `/onboarding/intent?intent=${encodeURIComponent(intentHint)}` : '/onboarding/intent';

        setVerifyEmail(email);
        setPendingRedirect(dest);
        setShowVerify(true);
        return;
      }
    } catch (error) {
      const msg = error.message || 'Something went wrong. Please try again.';
      // Only blame the user's internet if the request truly never left the browser.
      // Otherwise surface the server's real detail message (e.g. "Email already registered").
      if ((msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('TypeError')) && msg.length < 80) {
        toast.error('Connection hiccup — please try again in a moment. If it persists, refresh the page.');
      } else {
        toast.error(msg);
      }
      // Always log the raw error for easier support / debugging
      // eslint-disable-next-line no-console
      console.error('[AuthPage]', mode, 'error:', error);
      // Ship failure telemetry so ops gets notified (fire-and-forget, same origin).
      if (mode === 'register') {
        try {
          fetch(`${API}/telemetry/signup-failed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              mode,
              error_message: msg,
              error_stage: 'auth_page_submit',
              user_agent: navigator.userAgent,
              url: window.location.href,
            }),
            keepalive: true,
          });
        } catch { /* swallow — telemetry must never break UX */ }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Logo */}
          <h1 className="font-syne text-2xl font-bold text-center mb-2">
            <span className="text-white">ENTREPRE</span>
            <span className="text-holographic">NATIVES</span>
          </h1>
          <p className="text-stone-400 text-center font-outfit mb-8">
            {mode === 'login' ? 'Welcome back' : 'Create your account'}
          </p>

          <motion.form
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >

            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-stone-300 font-outfit">First Name</Label>
                  <div className="relative mt-2">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                    <Input
                      type="text"
                      value={firstName}
                      onChange={(e) => { setFirstName(e.target.value); fireLead(); }}
                      placeholder="Jane"
                      className="input-field w-full pl-12"
                      required
                      data-testid="first-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-stone-300 font-outfit">Last Name</Label>
                  <div className="relative mt-2">
                    <Input
                      type="text"
                      value={lastName}
                      onChange={(e) => { setLastName(e.target.value); fireLead(); }}
                      placeholder="Doe"
                      className="input-field w-full"
                      required
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-stone-300 font-outfit">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); fireLead(); }}
                  placeholder="you@example.com"
                  className="input-field w-full pl-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-stone-300 font-outfit">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field w-full pl-12 pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {mode === 'login' && (
              <div className="text-right -mt-3">
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotStep('email'); }}
                  className="text-[#A78BFA] hover:text-[#C4B5FD] text-sm font-outfit transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {mode === 'register' && (
              <p className="text-stone-400 text-xs font-outfit -mt-2">
                You'll choose how you want to use EntrepreNatives (shop · sell · build) on the next step.
              </p>
            )}

            {mode === 'register' && (
              <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-stone-700 bg-stone-900 text-yellow-400 focus:ring-yellow-400"
                  data-testid="terms-checkbox"
                />
                <span className="text-stone-400 text-xs font-outfit leading-relaxed">
                  I agree to the{' '}
                  <Link to="/legal/terms-of-service" target="_blank" className="text-yellow-400 hover:underline">Terms of Service</Link>,{' '}
                  <Link to="/legal/privacy-policy" target="_blank" className="text-yellow-400 hover:underline">Privacy Policy</Link>, and{' '}
                  <Link to="/legal/vendor-agreement" target="_blank" className="text-yellow-400 hover:underline">Platform Agreement</Link>.
                </span>
              </label>
            )}

            {mode === 'register' && (
              <label className="flex items-start gap-3 cursor-pointer" data-testid="newsletter-checkbox-label">
                <input
                  type="checkbox"
                  checked={subscribeNewsletter}
                  onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-stone-700 bg-stone-900 text-yellow-400 focus:ring-yellow-400"
                  data-testid="newsletter-consent-checkbox"
                />
                <span className="text-stone-400 text-xs font-outfit leading-relaxed">
                  Subscribe to our weekly newsletter — get founding-vendor spotlights, community wins, and platform updates. Unsubscribe anytime.
                </span>
              </label>
            )}

            <Button 
              type="submit" 
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black h-12 font-bold"
              disabled={loading}
              data-testid="auth-submit"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Free Account'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {mode === 'register' && (
              <p className="text-center text-stone-600 text-xs font-outfit">
                Free to join — no credit card needed until you're ready to purchase
              </p>
            )}

            <p className="text-center text-stone-500 text-sm font-outfit">
              {mode === 'login' ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="text-yellow-400 hover:underline font-medium"
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </motion.form>
        </motion.div>
      </div>

      {/* Right Panel - Hero Image */}
      <div className="hidden lg:flex flex-1 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1200)' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/80 to-transparent"></div>
        <div className="relative z-10 flex flex-col justify-center p-12 max-w-lg">
          <h2 className="font-syne text-4xl font-bold text-white mb-4">
            Join the Indigenous<br />Business Community
          </h2>
          <p className="text-stone-300 font-outfit">
            Whether you're looking to hire talented vendors or grow your own business, EntrepreNatives connects you with a trusted network of Indigenous professionals.
          </p>
        </div>
      </div>

      {/* Email Verification Dialog */}
      {showVerify && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-stone-900 border border-stone-800 rounded-2xl p-6 max-w-sm w-full"
          >
            <h2 className="font-syne text-xl font-bold text-white text-center mb-2">Verify Your Email</h2>
            <p className="text-stone-400 text-sm text-center mb-1">We sent a 6-digit code to</p>
            <p className="text-white text-sm text-center font-bold mb-4">{verifyEmail}</p>
            <p className="text-stone-500 text-xs text-center mb-4">Check your email inbox for the verification code</p>
            {localStorage.getItem('my_promo_code') && (
              <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 rounded-xl p-3 mb-4 text-center" data-testid="promo-code-onboard">
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Your Referral Code</p>
                <p className="text-white font-mono font-bold text-lg tracking-[0.15em]">{localStorage.getItem('my_promo_code')}</p>
                <p className="text-[10px] text-[#9CA3AF] mt-1">Share it with friends to earn Reward Credits!</p>
              </div>
            )}
            <Input
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="input-field w-full text-center text-lg tracking-widest mb-4"
              maxLength={6}
              data-testid="verify-code-input"
            />
            <Button
              onClick={async () => {
                setVerifying(true);
                try {
                  const API = process.env.REACT_APP_BACKEND_URL + '/api';
                  const res = await fetch(`${API}/auth/verify-email`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: verifyEmail, code: verifyCode })
                  });
                  if (res.ok) {
                    toast.success('Email verified!');
                    setShowVerify(false);
                    if (pendingRedirect) navigate(pendingRedirect, { replace: true });
                  } else {
                    const d = await res.json();
                    toast.error(d.detail || 'Invalid code');
                  }
                } catch { toast.error('Verification failed'); }
                finally { setVerifying(false); }
              }}
              disabled={verifyCode.length !== 6 || verifying}
              className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold"
              data-testid="verify-submit"
            >
              {verifying ? 'Verifying...' : 'Verify Email'}
            </Button>
            <button
              onClick={() => { setShowVerify(false); if (pendingRedirect) navigate(pendingRedirect, { replace: true }); }}
              className="w-full mt-3 text-stone-500 text-sm hover:text-stone-300 transition-colors"
              data-testid="skip-verify"
            >
              Skip for now
            </button>
          </motion.div>
        </div>
      )}

      {/* Forgot Password Dialog */}
      {showForgot && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-stone-900 border border-stone-800 rounded-2xl p-6 max-w-sm w-full"
          >
            {forgotStep === 'email' && (
              <>
                <h2 className="font-syne text-xl font-bold text-white text-center mb-2">Reset Password</h2>
                <p className="text-stone-400 text-sm text-center mb-6">Enter your email and we'll send you a reset code</p>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-field w-full mb-4"
                  data-testid="forgot-email-input"
                />
                <Button
                  onClick={async () => {
                    if (!forgotEmail.trim()) { toast.error('Enter your email'); return; }
                    setForgotLoading(true);
                    try {
                      const res = await fetch(`${API}/auth/forgot-password`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: forgotEmail })
                      });
                      if (res.ok) {
                        toast.success('Reset code sent! Check your email.');
                        setForgotStep('code');
                      } else {
                        const d = await res.json();
                        toast.error(d.detail || 'Failed to send reset code');
                      }
                    } catch { toast.error('Something went wrong'); }
                    finally { setForgotLoading(false); }
                  }}
                  disabled={forgotLoading}
                  className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold"
                  data-testid="forgot-send-code"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </>
            )}

            {forgotStep === 'code' && (
              <>
                <h2 className="font-syne text-xl font-bold text-white text-center mb-2">Enter Reset Code</h2>
                <p className="text-stone-400 text-sm text-center mb-1">We sent a code to</p>
                <p className="text-white text-sm text-center font-bold mb-4">{forgotEmail}</p>
                <Input
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="input-field w-full text-center text-lg tracking-widest mb-3"
                  maxLength={6}
                  data-testid="reset-code-input"
                />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className="input-field w-full mb-4"
                  data-testid="new-password-input"
                />
                <Button
                  onClick={async () => {
                    if (resetCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
                    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                    setForgotLoading(true);
                    try {
                      const res = await fetch(`${API}/auth/reset-password`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: forgotEmail, code: resetCode, new_password: newPassword })
                      });
                      if (res.ok) {
                        toast.success('Password reset! You can now log in.');
                        setForgotStep('done');
                      } else {
                        const d = await res.json();
                        toast.error(d.detail || 'Failed to reset password');
                      }
                    } catch { toast.error('Something went wrong'); }
                    finally { setForgotLoading(false); }
                  }}
                  disabled={forgotLoading}
                  className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold"
                  data-testid="reset-password-submit"
                >
                  {forgotLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </>
            )}

            {forgotStep === 'done' && (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="font-syne text-xl font-bold text-white mb-2">Password Reset!</h2>
                <p className="text-stone-400 text-sm mb-6">You can now log in with your new password.</p>
                <Button
                  onClick={() => { setShowForgot(false); setMode('login'); setEmail(forgotEmail); setPassword(''); }}
                  className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold"
                  data-testid="back-to-login"
                >
                  Back to Login
                </Button>
              </div>
            )}

            {forgotStep !== 'done' && (
              <button
                onClick={() => setShowForgot(false)}
                className="w-full mt-3 text-stone-500 text-sm hover:text-stone-300 transition-colors"
                data-testid="cancel-forgot"
              >
                Cancel
              </button>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
